import React, { useRef, useEffect, useCallback } from 'react';
import { Input, Slider, Typography } from 'antd';
import { unicodeLabel, buildRoundedPath } from './glyphEditorState';

const { Text } = Typography;

/**
 * SentencePreviewView - Type text and see it rendered, adjust typographic properties.
 * Inspired by Runebender's text preview view.
 */
const SentencePreviewView = ({
    glyphs, metadata, previewText, onPreviewTextChange,
    fontSize, onFontSizeChange,
    letterSpacing, onLetterSpacingChange,
    lineHeight, onLineHeightChange,
    hoveredChar, onHoveredCharChange,
}) => {
    const canvasRef = useRef(null);
    const upm = metadata.unitsPerEm || 1000;

    // Render preview text using glyph paths on canvas
    const renderPreview = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, rect.width, rect.height);

        const scale = fontSize / upm;
        const baselineY = metadata.ascender * scale + 60;
        let cursorX = 40;

        const lines = previewText.split('\n');
        let lineY = baselineY;

        for (const line of lines) {
            cursorX = 40;
            for (let i = 0; i < line.length; i++) {
                const charCode = line.charCodeAt(i);
                const glyph = glyphs.find(g => g.unicode === charCode);

                if (glyph && glyph.path && glyph.path.length > 0) {
                    // Apply corner rounding before rendering
                    const displayPath = buildRoundedPath(glyph.path);
                    ctx.save();
                    ctx.translate(cursorX, lineY);
                    ctx.scale(scale, -scale);
                    ctx.beginPath();

                    for (const cmd of displayPath) {
                        switch (cmd.type) {
                            case 'M': ctx.moveTo(cmd.x, cmd.y); break;
                            case 'L': ctx.lineTo(cmd.x, cmd.y); break;
                            case 'C': ctx.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y); break;
                            case 'Q': ctx.quadraticCurveTo(cmd.x1, cmd.y1, cmd.x, cmd.y); break;
                            case 'Z': ctx.closePath(); break;
                        }
                    }
                    ctx.fillStyle = '#e6edf3';
                    ctx.fill();
                    ctx.restore();
                    cursorX += (glyph.advanceWidth || 600) * scale + letterSpacing;
                } else {
                    // No custom path — render with system font as fallback
                    ctx.save();
                    ctx.font = `${fontSize}px "Inter", "SF Pro Display", system-ui, sans-serif`;
                    ctx.fillStyle = '#484f58';
                    ctx.textBaseline = 'alphabetic';
                    ctx.fillText(line[i], cursorX, lineY);
                    const measured = ctx.measureText(line[i]);
                    ctx.restore();
                    cursorX += measured.width + letterSpacing;
                }
            }
            lineY += fontSize * (lineHeight || 1.4);
        }
    }, [glyphs, previewText, fontSize, letterSpacing, lineHeight, metadata, upm]);

    useEffect(() => { renderPreview(); }, [renderPreview]);

    // Find hovered char info
    const hoveredGlyph = hoveredChar != null
        ? glyphs.find(g => g.unicode === hoveredChar)
        : null;

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#8b949e', fontSize: 11, width: 70, flexShrink: 0, textAlign: 'right' }}>{label}</span>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1 }}
                styles={{ track: { background: '#1f6feb' }, rail: { background: '#30363d' } }}
            />
            <span style={{ color: '#58a6ff', fontSize: 11, fontFamily: 'monospace', width: 40, textAlign: 'right' }}>{value}</span>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Preview Canvas */}
            <div style={{ flex: 1, background: '#0d1117', overflow: 'hidden', position: 'relative' }}>
                <canvas
                    ref={canvasRef}
                    style={{ width: '100%', height: '100%', display: 'block' }}
                />
            </div>

            {/* Bottom Controls */}
            <div style={{
                borderTop: '1px solid #30363d', background: '#161b22',
                padding: 16, display: 'flex', flexDirection: 'column', gap: 12, flexShrink: 0,
            }}>
                {/* Text Input */}
                <Input.TextArea
                    value={previewText}
                    onChange={e => onPreviewTextChange(e.target.value)}
                    placeholder="Type something..."
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    style={{ background: '#0d1117', border: '1px solid #30363d', color: '#e6edf3', borderRadius: 6, fontSize: 16 }}
                />

                {/* Sliders */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {sliderRow('Size', 12, 200, fontSize, onFontSizeChange)}
                    {sliderRow('Spacing', -20, 50, letterSpacing, onLetterSpacingChange)}
                    {sliderRow('Leading', 0.8, 3, lineHeight, onLineHeightChange, 0.1)}
                </div>

                {/* Hovered character info bar */}
                {hoveredGlyph && (
                    <div style={{
                        display: 'flex', gap: 16, alignItems: 'center',
                        padding: '8px 12px', background: '#0d1117', borderRadius: 6,
                        border: '1px solid #30363d',
                    }}>
                        <div style={{ fontSize: 28, color: '#e6edf3', fontFamily: 'system-ui', width: 40, textAlign: 'center' }}>
                            {String.fromCharCode(hoveredGlyph.unicode)}
                        </div>
                        {[
                            ['Name', hoveredGlyph.name],
                            ['Unicode', unicodeLabel(hoveredGlyph.unicode)],
                            ['Width', hoveredGlyph.advanceWidth],
                        ].map(([k, v]) => (
                            <div key={k}>
                                <div style={{ fontSize: 9, color: '#484f58' }}>{k}</div>
                                <div style={{ fontSize: 12, color: '#c9d1d9', fontFamily: 'monospace' }}>{v}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SentencePreviewView;
